import React, { useState } from 'react';

import { useManagementPackContext } from './ManagementPackContext';
import { SNAPSHOT_COMPLETED } from './managementPackUtils';
import FormField from './tabs/FormField';

import Alert from 'CommonComponents/Alert';
import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';

const ISSUED_TO_MAX = 255;

/**
 * Why the pack cannot be issued yet, or null when it can.
 */
export const issueBlocker = ({ activePack, preview, options }) => {
    if (!activePack || activePack.isIssued) {
        return $t('Generate the pack before issuing it.');
    }

    if (activePack.snapshotStatus !== SNAPSHOT_COMPLETED) {
        return $t('The pack can be issued once its data has been captured.');
    }

    if (preview?.overCap) {
        return $t('This pack has too many slides to issue. Turn off some sections to bring it under the limit of {{max}}.', { max: options.maxSlides });
    }

    return null;
};

/**
 * Issue the pack without exporting it, optionally naming who it is issued to.
 * Issuing locks the pack for good, so the dialog says so before it is confirmed.
 */
const IssuePackModal = ({ modal }) => {
    const { activePack, issuing, issuePack } = useManagementPackContext();
    const [issuedTo, setIssuedTo] = useState('');

    const close = () => {
        setIssuedTo('');
        modal.hide();
    };

    const issue = () => {
        issuePack(issuedTo).then((issued) => {
            if (issued) {
                close();
            }
        });
    };

    return (
        <Modal modal={modal}>
            <ModalHeader title={$t('Issue management pack')} close={close} dark />
            <div className="tw-flex tw-flex-col tw-gap-4 tw-text-sm tw-text-charcoal">
                <p className="tw-m-0">
                    {activePack?.revision > 1
                        ? $t('Issuing revision {{revision}} locks its figures, statuses and commentary, and marks the revision it replaces as superseded.', {
                              revision: activePack.revision,
                          })
                        : $t('Issuing locks the pack: its figures, statuses and commentary can no longer be changed or regenerated.')}
                </p>
                <Alert type="warning">{$t('This cannot be undone. A correction needs a reissue.')}</Alert>
                <FormField label={$t('Issued to (optional)')} htmlFor="mp-issued-to">
                    <input
                        id="mp-issued-to"
                        type="text"
                        className="text-input tw-border-0 tw-w-full"
                        maxLength={ISSUED_TO_MAX}
                        placeholder={$t('e.g. Board, 15 Oct 2026 meeting')}
                        value={issuedTo}
                        disabled={issuing}
                        onChange={(event) => setIssuedTo(event.target.value)}
                    />
                </FormField>
                <p className="tw-m-0 tw-text-charcoal-75">{$t('Export the pack afterwards for a final copy without the draft watermark.')}</p>
            </div>
            <ModalFooter
                handleSubmit={issue}
                onCancel={close}
                isSubmitting={issuing}
                submitButtonVariant="primary"
                primaryButtonText={$t('Issue pack')}
                cancelButtonText={$t('Cancel')}
            />
        </Modal>
    );
};

export default IssuePackModal;
